import React, { createContext, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AppToast } from 'components/toast';
import { AppViews } from 'endpoints';
import qs from 'qs';
import i18n from 'i18n';
import { groupBy, noop } from 'lodash';
import moment from 'moment';
import { IStore } from 'store/reducers';
import { closeTrip, setIsTripSelected, setLoading } from 'store/actions';
import { AuthState, Passengers, PaymentsState, SearchForm, SubscriberState } from 'store/types';
import { CheckoutProps, CreditCardValuesBR } from '../models/checkout.model';
import { MercadoPagoError } from '../models/mercadoPago.model';
import { ItemType } from '../models/selectableItems.model';

const defaultPassengers: Passengers = {
  adult: 0,
  child: 0,
  baby: 0,
};

const defaultCreditCard: CreditCardValuesBR = {
  cardNumber: '',
  cvc: '',
  holderName: '',
  identificationNumber: '',
  installments: 0,
};

const defaultValues: CheckoutProps = {
  payment: null,
  paymentMethods: [],
  departDate: null,
  returnDate: null,
  passengers: defaultPassengers,
  token: '',
  isSubscribed: false,
  handleGoBack: noop,
  handleCloseTrip: noop,
  mercadoPagoToken: '',
  setMercadoPagoToken: noop,
  creditCard: defaultCreditCard,
  setCreditCard: noop,
  fieldErrors: [],
  setFieldErrors: noop,
  setPrice: noop,
  finalPrice: '',
  valuePaymentDiffCreditAndTripTotal: 0,
  selectableItem: ItemType.NONE,
  setCreditRebooking: noop,
  creditRebooking: 0,
  priceDiscountRemarked: 0,
  setPriceDiscountRemarked: noop,
};

export const CheckoutContext = createContext<CheckoutProps>(defaultValues);

export const CheckoutProvider: React.FC = ({ children }) => {
  const [priceDiscountRemarked, setPriceDiscountRemarked] = useState(0);
  const [creditRebooking, setCreditRebooking] = useState<number | any>(0);
  const { token } = useSelector<IStore, AuthState>((state) => state.auth);
  const { isSubscribed } = useSelector<IStore, SubscriberState>((state) => state.subscription);
  const { paymentMethods, payment } = useSelector<IStore, PaymentsState>((state) => state.payments);
  const { departDate, returnDate, passengers } = useSelector<IStore, SearchForm>((state) => state.search.searchForm);

  const tripPriceWithoutExtras = useMemo(
    () =>
      payment?.trips
        .reduce((prev, current) => {
          const grouped = groupBy(current.tickets, (ticket) => ticket.connectionIndex);
          const tickets = grouped[0];

          const ticketsPrice = tickets.reduce((acc, { tripPrice }) => acc + parseFloat(tripPrice), 0);

          return prev + ticketsPrice;
        }, 0)
        .toFixed(2),
    [payment],
  );

  const valuePaymentDiffCreditAndTripTotal = useMemo(() => {
    const v1 = Number(tripPriceWithoutExtras);
    const v2 = Number(creditRebooking);
    const valueDiff = v1 - v2;
    if (valueDiff) {
      return valueDiff;
    }
    return 0;
  }, [tripPriceWithoutExtras, creditRebooking]);

  const history = useHistory();
  const dispatch = useDispatch();

  const [mercadoPagoToken, setMercadoPagoToken] = useState('');

  const [fieldErrors, setFieldErrors] = useState<Array<MercadoPagoError>>([]);
  const [creditCard, setCreditCard] = useState(defaultCreditCard);
  const [price, setPrice] = useState(payment?.price);

  const getSearchUrl = React.useCallback(() => {
    if (!payment) {
      return AppViews.HOME;
    }
    const departureDate = departDate ? `&departDate=${moment(departDate).format('YYYY-MM-DD')}` : `''`;
    const returningDate = returnDate ? `&returnDate=${moment(returnDate).format('YYYY-MM-DD')}` : '';

    if (payment.trips.length > 1) {
      const { departureLocation, arrivalLocation } = payment.trips[payment.trips.length - 1];

      return `${AppViews.SEARCH}?departureLocation=${departureLocation.externalId}&arrivalLocation=${
        arrivalLocation.externalId
      }${departureDate}${returningDate}&${qs.stringify({ passengers })}`;
    }

    const { departureLocation, arrivalLocation } = payment.trips[0];

    return `${AppViews.SEARCH}?departureLocation=${departureLocation.externalId}&arrivalLocation=${
      arrivalLocation.externalId
    }${departureDate}${returningDate}&${qs.stringify({ passengers })}`;
  }, [departDate, passengers, payment, returnDate]);

  const handleGoBack = () => {
    if (!payment) return;

    dispatch(setIsTripSelected(payment.trips.length > 1 ? 'returnTrip' : 'departureTrip', false));

    history.push(getSearchUrl());
  };

  const handleCloseTrip = async (tripId: string) => {
    try {
      dispatch(setLoading(true));
      await dispatch(closeTrip.request({ id: tripId }));

      dispatch(setLoading(false));
    } catch (err) {
      dispatch(setLoading(false));
      AppToast.error(i18n.t('validations:somethingWentWrong'));
    }
  };

  const finalPrice = useMemo(() => price ?? payment?.price ?? '0', [payment, price]);
  const checkoutContext = {
    payment,
    paymentMethods,
    departDate,
    returnDate,
    passengers,
    token,
    isSubscribed,
    handleGoBack,
    handleCloseTrip,
    mercadoPagoToken,
    setMercadoPagoToken,
    creditCard,
    setCreditCard,
    fieldErrors,
    setFieldErrors,
    setPrice,
    finalPrice,
    selectableItem: ItemType.NONE,
    valuePaymentDiffCreditAndTripTotal,
    priceDiscountRemarked,
    setCreditRebooking,
    setPriceDiscountRemarked,

    creditRebooking,
  };

  return <CheckoutContext.Provider value={checkoutContext}>{children}</CheckoutContext.Provider>;
};
